// src/rms/pages/Occupancy.js
// ============================================================================
// Occupancy board — continuous-occupancy verticals (storage lockers)
// ============================================================================
// Storage has no trips, so there is no "out now" list to show. What matters is
// which units are occupied, by whom, since when, whether rent is current, and
// what access credentials are live. Moving a tenant out revokes their codes.
// ============================================================================

import React, { useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import { useCollection, indexById } from "../hooks/useCollection";
import { optionsForField, optionLabel } from "../verticals/presets";
import rentalService from "../data/rentalService";
import { PageHeader, Stat, StatusPill, Modal, Field, Select, Banner, EmptyState, formatDate, AssetCell, assetLabel, DemoPill } from "../components/ui";
import adEngine from "../ads/adEngine";

function Occupancy() {
  const { operator, config, t, tLower, formatMoney, invalidate } = useRms();
  const unitSizes = useMemo(() => optionsForField(config, "unit_size"), [config]);
  const { rows: occupancies } = useCollection("occupancies", {}, { orderBy: "moved_in_at", direction: "desc" });
  const { rows: assets } = useCollection("assets");
  const { rows: customers } = useCollection("customers");
  const { rows: subscriptions } = useCollection("subscriptions");
  const { rows: credentials } = useCollection("accessCredentials");

  const [movingOut, setMovingOut] = useState(null);
  const [error, setError] = useState(null);

  const assetsById = useMemo(() => indexById(assets), [assets]);
  const customersById = useMemo(() => indexById(customers), [customers]);
  const subsById = useMemo(() => indexById(subscriptions), [subscriptions]);

  const live = occupancies.filter((o) => ["active", "notice_given"].includes(o.status));
  const vacant = assets.filter((a) => a.status === "available");
  const delinquent = subscriptions.filter((s) => s.status === "past_due");

  const handleMoveOut = async (occupancy, unitCondition, notes) => {
    adEngine.recordAction();
    const result = await rentalService.moveOut(operator, {
      occupancyId: occupancy.id,
      unitCondition,
      notes,
    });
    if (!result.success) setError(result.error);
    setMovingOut(null);
    invalidate();
  };

  return (
    <>
      <PageHeader
        title={t("occupancy.occupancy", "Occupancy")}
        subtitle={t("occupancy.subtitle", "{occupied} occupied · {vacant} vacant", {
          occupied: live.length,
          vacant: vacant.length,
        })}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {delinquent.length ? (
        <Banner tone="warn">
          <strong>{delinquent.length} past due.</strong>
          {t("occupancy.rentIsUnpaidBeyond", "Rent is unpaid beyond the grace period. Lien status is tracked per lease below.")}
        </Banner>
      ) : null}

      <div className="rms-stat-row">
        <Stat label={t("occupancy.occupied", "Occupied")} value={live.length} />
        <Stat label={t("occupancy.vacant", "Vacant")} value={vacant.length} />
        <Stat
          label={t("occupancy.occupancyRate", "Occupancy rate")}
          value={assets.length ? `${Math.round((live.length / assets.length) * 100)}%` : "—"}
        />
        <Stat
          label={t("occupancy.monthlyRentRoll", "Monthly rent roll")}
          value={formatMoney(
            subscriptions.filter((s) => ["active", "past_due"].includes(s.status)).reduce((sum, s) => sum + s.amount_cents, 0)
          )}
        />
      </div>

      {live.length === 0 ? (
        <EmptyState
          title={t("occupancy.noUnitsOccupied", "No units occupied")}
          message={t("occupancy.empty.body", "Book a unit to a {tenant} and complete the move-in to start a lease.", {
            tenant: tLower("customer", "tenant"),
          })}
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("accessLog.unit", "Unit")}</th>
                <th>{t("customer", "Tenant")}</th>
                <th>{t("occupancy.movedIn", "Moved in")}</th>
                <th className="rms-num">{t("occupancy.rent", "Rent")}</th>
                <th>{t("occupancy.nextBill", "Next bill")}</th>
                <th>{t("occupancy.access", "Access")}</th>
                <th>{t("campaignForm.status", "Status")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {live.map((o) => {
                const asset = assetsById[o.asset_id];
                const customer = customersById[o.customer_id];
                const sub = subsById[o.subscription_id];
                const liveCreds = credentials.filter((c) => c.occupancy_id === o.id && !c.revoked_at);
                return (
                  <tr key={o.id}>
                    <td>
                      {o.is_demo ? <DemoPill /> : null}
                      <AssetCell
                        asset={asset}
                        extra={`${
                          optionLabel(unitSizes, asset?.custom_fields?.unit_size) || "—"
                        } · zone ${asset?.custom_fields?.gate_zone || "—"}`}
                      />
                    </td>
                    <td>{customer ? `${customer.first_name} ${customer.last_name || ""}`.trim() : "—"}</td>
                    <td style={{ fontSize: 12.5 }}>{formatDate(o.moved_in_at)}</td>
                    <td className="rms-num">{sub ? formatMoney(sub.amount_cents) : "—"}</td>
                    <td style={{ fontSize: 12.5 }}>{sub ? formatDate(sub.next_bill_date) : "—"}</td>
                    <td style={{ fontSize: 12.5 }}>
                      {liveCreds.length ? (
                        liveCreds.map((c) => (
                          <div key={c.id}>
                            <code style={{ fontSize: 12 }}>{c.credential_value}</code>
                            <span style={{ color: "var(--rms-muted)" }}> {c.credential_type.replace(/_/g, " ")}</span>
                          </div>
                        ))
                      ) : (
                        <span style={{ color: "var(--rms-muted)" }}>none issued</span>
                      )}
                    </td>
                    <td>
                      {sub?.status === "past_due" ? (
                        <>
                          <StatusPill status="past_due" label="past due" />
                          {sub.lien_status && sub.lien_status !== "none" ? (
                            <div style={{ fontSize: 11, color: "var(--rms-warn)", marginTop: 3 }}>
                              {sub.lien_status.replace(/_/g, " ")}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <StatusPill status={o.status} />
                      )}
                    </td>
                    <td className="rms-num">
                      <button type="button" className="rms-btn rms-btn--ghost rms-btn--sm" onClick={() => setMovingOut(o)}>
                        {t("occupancy.moveOut", "Move out")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {movingOut ? (
        <MoveOutModal
          occupancy={movingOut}
          asset={assetsById[movingOut.asset_id]}
          customer={customersById[movingOut.customer_id]}
          onCancel={() => setMovingOut(null)}
          onConfirm={handleMoveOut}
        />
      ) : null}
    </>
  );
}

function MoveOutModal({ occupancy, asset, customer, onCancel, onConfirm }) {
  const { t } = useRms();
  const [condition, setCondition] = useState("clean");
  const [notes, setNotes] = useState("");

  return (
    <Modal
      title={`Move out — ${assetLabel(asset)}`}
      onClose={onCancel}
      width={480}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button type="button" className="rms-btn rms-btn--primary" onClick={() => onConfirm(occupancy, condition, notes)}>
            {t("occupancy.completeMoveOut", "Complete move-out")}
          </button>
        </>
      }
    >
      <p style={{ fontSize: 13.5, marginTop: 0, color: "var(--rms-muted-strong)" }}>
        {customer ? `${customer.first_name} ${customer.last_name || ""}`.trim() : t("occupancy.thisTenant", "This tenant")} has
        been in this unit since {formatDate(occupancy.moved_in_at)}.
      </p>

      <Banner tone="warn">
        {t("occupancy.completingThisRevokesEvery", "Completing this revokes every access credential issued for this unit and ends the monthly lease. The unit becomes available immediately.")}
      </Banner>

      <Field label={t("occupancy.unitCondition", "Unit condition")}>
        <Select
          value={condition}
          onChange={setCondition}
          placeholder={t("occupancy.cleanAndEmpty", "Clean and empty")}
          options={[
            { value: "clean", label: "Clean and empty — ready to re-let", labelKey: "opt.unitCondition.clean" },
            { value: "needs_cleaning", label: "Needs cleaning or repair", labelKey: "opt.unitCondition.needsCleaning" },
          ]}
        />
      </Field>
      <Field label={t("occupancy.notes", "Notes")}>
        <textarea className="rms-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth recording…" />
      </Field>
    </Modal>
  );
}

export default Occupancy;
